import { EVENT_CATEGORY_COLORS, type LimitesDuSpot, type SiroccoEvent } from '@sirocco/shared';
import type { RefObject } from 'react';
import { t } from '../../../i18n';

/**
 * Ce qu'un fond de carte doit savoir faire pour l'ecran des sorties.
 *
 * Deux fonds le font : la carte Apple, par defaut, et celle
 * d'OpenStreetMap, en secours quand Apple ne repond pas. L'ecran ne
 * connait ni l'un ni l'autre : il leur donne ce qu'il sait, et ils le
 * posent.
 */

export type Lieu = { lat: number; lon: number };
export type SortiePlacee = SiroccoEvent & Lieu;

export interface PoigneeDeCarte {
  /** Revenir sur ma position. */
  recentrer: () => void;
  /**
   * Aller voir un point : une sortie choisie dans le tiroir ou sur la
   * carte. `partCachee` dit quelle fraction du bas de la carte la fiche
   * recouvre, pour que le point se pose au-dessus d'elle.
   */
  viser: (lieu: Lieu, partCachee?: number) => void;
  /**
   * Se rapprocher jusqu'a ce que ces points-la se separent.
   *
   * C'est la reponse a l'appui sur un point qui en compte plusieurs :
   * plutot que d'en faire la liste, on va voir. La vue se serre sur eux
   * avec assez de marge pour qu'aucun ne se touche plus.
   */
  approcher: (lieux: Lieu[]) => void;
}

export interface ProprietesDeCarte {
  sorties: SortiePlacee[];
  /** `undefined` tant qu'on ne sait pas, `null` si l'on ne saura pas. */
  moi: Lieu | null | undefined;
  /** Mon point compte pour le cadrage : je suis dans la ville. */
  moiDansLaVille: boolean;
  /** Ou s'ouvrir, faute de mieux. */
  centreVille: Lieu | null;
  /** Sur quelle largeur, en degres : une ville, ou un campus. */
  etendueVille?: number;
  /** Un campus : la carte s'ouvre en relief, et n'en sort pas. */
  relief?: boolean;
  /** Le territoire du spot : on ne glisse ni ne recule au-dela. */
  limites?: LimitesDuSpot;
  /** Le nom pose sur le campus, en relief : celui du serveur. */
  nomDuLieu?: string;
  nuit: boolean;
  onChoisir: (sortie: SiroccoEvent | null) => void;
  /** Plusieurs sorties sous un meme point : l'ecran decide quoi en faire. */
  onGroupe: (sorties: SiroccoEvent[]) => void;
  /**
   * Ce qui tient dans la vue, a chaque fois que cela change.
   *
   * Le tiroir du bas ne parle que de ce qu'on voit : son compte suit donc
   * la carte, et sa liste aussi. Dezoomer fait monter le nombre.
   */
  onVue: (ids: string[]) => void;
  /**
   * Le membre vient de prendre la carte en main : appui, glissement,
   * pincement.
   *
   * Tout ce qui etait pose dessus s'efface — la fiche d'une sortie, le
   * tiroir deplie. On veut voir la carte : c'est pour cela qu'on la
   * touche.
   */
  onGeste: () => void;
  /** Le fond ne peut pas s'afficher : l'ecran passe a l'autre, ou le dit. */
  onPanne: (message: string) => void;
  poignee: RefObject<PoigneeDeCarte | null>;
}

/* Le quartier, pas la ville : on arrive chez soi. */
export const ZOOM_MOI = 14.5;
export const ZOOM_VILLE = 11;
/** Le plus pres ou l'on se pose sur un point touche : la rue et ses portes. */
export const ZOOM_PRES = 17.5;
/** Faute de ville connue : la France entiere, le temps que le reste arrive. */
export const PAR_DEFAUT: Lieu = { lat: 46.6, lon: 2.4 };

/**
 * La pastille d'une sortie : la couleur de sa categorie, cerclee de blanc.
 * Le meme element pour les deux fonds.
 *
 * Une sortie spontanee prend le rose plutot que la couleur de sa
 * categorie : sur une carte, « ca se passe maintenant » se remarque avant
 * « c'est du sport ».
 */
/*
  La sortie dont l'epingle est levee.

  Les epingles sont refaites a chaque zoom : sans memoire, celle qu'on
  vient de toucher retomberait des que la carte bouge. On retient donc
  laquelle est levee, et une epingle neuve se leve si c'est la sienne.
*/
let sortieLevee: string | null = null;

/** Leve l'epingle de cette sortie, et pose toutes les autres. */
export function leverLEpingle(id: string | null): void {
  sortieLevee = id;
  document.querySelectorAll<HTMLElement>('.ev-carte__sortie').forEach((epingle) => {
    epingle.classList.toggle('ev-carte__sortie--levee', id !== null && epingle.dataset.sortie === id);
  });
}

export function pastilleDe(sortie: SiroccoEvent, onChoisir: () => void): HTMLButtonElement {
  const pastille = document.createElement('button');
  pastille.dataset.sortie = sortie.id;
  if (sortie.id === sortieLevee) pastille.classList.add('ev-carte__sortie--levee');
  pastille.type = 'button';
  pastille.classList.add('ev-carte__sortie');
  if (sortie.spontane) pastille.classList.add('ev-carte__sortie--spontane');
  /* La couleur se pose ici, sur l'element : une valeur en ligne l'emporte
     sur la feuille de style, donc une regle de classe ne suffirait pas. */
  pastille.style.setProperty(
    '--cat',
    sortie.spontane ? 'var(--spontane)' : EVENT_CATEGORY_COLORS[sortie.category],
  );
  pastille.setAttribute(
    'aria-label',
    t.carte.pastilles.sortie(sortie.title, t.sorties.categories[sortie.category], sortie.spontane),
  );
  /*
    Une punaise : une tete ronde en relief, a la couleur de la categorie,
    sur une aiguille d'acier fine. Elle se plante par la pointe de son
    aiguille — chaque moteur l'y pose — et laisse voir la rue ou le
    batiment autour. Le relief et le metal viennent de degrades poses par
    la feuille de style : voir `.ev-punaise`.
  */
  pastille.appendChild(punaise());
  pastille.addEventListener('click', (clic) => {
    /* Le fond prendrait le clic pour un appui a cote, qui referme la fiche. */
    clic.stopPropagation();
    /* Touchee, elle se leve : c'est elle qu'on regarde. */
    leverLEpingle(sortie.id);
    onChoisir();
  });
  return pastille;
}

/**
 * Une punaise : son ombre portee, puis elle — sa tete, ce qu'elle porte, et
 * son aiguille. L'ombre reste au sol, la punaise se leve d'un bloc.
 */
function punaise(contenu?: HTMLElement): DocumentFragment {
  const ombre = document.createElement('span');
  ombre.className = 'ev-punaise__ombre';
  const corps = document.createElement('span');
  corps.className = 'ev-punaise';
  const tete = document.createElement('span');
  tete.className = 'ev-punaise__tete';
  if (contenu) tete.appendChild(contenu);
  const aiguille = document.createElement('span');
  aiguille.className = 'ev-punaise__aiguille';
  corps.append(aiguille, tete);
  const morceaux = document.createDocumentFragment();
  morceaux.append(ombre, corps);
  return morceaux;
}

/*
  La taille des punaises suit le zoom.

  Une punaise garde sinon la meme taille a l'ecran pendant que la carte
  rapetisse : de loin, son aiguille couvrait un quartier, et sa tete — ce
  que l'oeil regarde — flottait au-dessus d'un autre endroit que celui
  ou elle est plantee. Elle rapetisse donc un peu avec la vue, comme un
  objet qu'on regarde de plus loin, sans descendre sous les quatre
  cinquiemes de sa taille : trop petite, on ne la voyait plus.
*/
const ECHELLE_MIN = 0.8;
const ECHELLE_MAX = 1.1;

/** Pose l'echelle des punaises sur le cadre de la carte : elles la lisent toutes. */
export function mettreLesPunaisesALEchelle(cadre: HTMLElement | null, echelle: number): void {
  if (!cadre) return;
  const bornee = Math.min(ECHELLE_MAX, Math.max(ECHELLE_MIN, echelle));
  cadre.style.setProperty('--echelle-punaise', bornee.toFixed(3));
}

/**
 * La pastille d'un groupe : combien de sorties tiennent sous ce point.
 *
 * Deux sorties au meme endroit se cachaient l'une l'autre — a l'ESSEC,
 * tout se passe sur trois cents metres. Elles se rassemblent donc en un
 * seul point qui dit leur nombre, et l'appui emmene la carte sur elles :
 * en se rapprochant, le groupe se defait tout seul. Ce n'est que
 * lorsqu'elles partagent vraiment l'adresse — la ou aucun zoom ne les
 * separera — que le tiroir s'ouvre pour les nommer.
 */
export function pastilleDeGroupe(combien: number, onChoisir: () => void): HTMLButtonElement {
  const pastille = document.createElement('button');
  pastille.type = 'button';
  pastille.className = 'ev-carte__groupe';
  /*
    La meme punaise que pour une sortie, a la tete plus grosse et aux
    couleurs du spot, le nombre dedans : un groupe se lit comme une
    punaise, pas comme un autre genre de repere.
  */
  const nombre = document.createElement('span');
  nombre.className = 'ev-punaise__nombre';
  nombre.textContent = String(combien);
  pastille.appendChild(punaise(nombre));
  pastille.setAttribute('aria-label', t.carte.pastilles.groupe(combien));
  pastille.addEventListener('click', (clic) => {
    clic.stopPropagation();
    onChoisir();
  });
  return pastille;
}

/** Mon rond bleu. */
export function rondDeMoi(): HTMLSpanElement {
  const rond = document.createElement('span');
  rond.className = 'ev-carte__moi';
  rond.setAttribute('aria-label', t.carte.pastilles.maPosition);
  return rond;
}
